import * as React from "react";
import { Link } from "react-router-dom";

import { listAgentRequests } from "@/api/client";
import { AgentRequestView } from "@/api/types";
import { ComicPanel } from "@/components/comic";
import { useFeature } from "@/features/profile/useFeature";

/**
 * A glance at who has asked for time, in the chat's right rail.
 *
 * <p>Additive: the wire above it and the agenda below it are untouched. It sits between them
 * because it is neither — the wire is live and momentary, the agenda is today, and this is a small
 * pile of things that will still be there tomorrow.</p>
 *
 * <p>A count and a link, and no decision. Deciding needs to see who asked and what they said, and
 * that belongs on the page that can show it rather than in a 280px column.</p>
 *
 * <p>The two counts are kept apart on purpose (D-94). One is an hour being held that runs out; the
 * other is somebody nothing covered, who was told nothing and is not waiting. Adding them would
 * produce a number that means neither.</p>
 */
export function AskedForTimePanel({
  /** Injected so the panel can be rendered in a test without a live feature fetch. */
  marketplace = undefined as ReturnType<typeof useFeature> | undefined,
  rows: given,
}: {
  marketplace?: ReturnType<typeof useFeature>;
  rows?: AgentRequestView[];
} = {}) {
  const feature = useFeature("A2A_MARKETPLACE");
  const state = marketplace ?? feature;
  const [rows, setRows] = React.useState<AgentRequestView[]>(given ?? []);

  React.useEffect(() => {
    if (given || state !== "granted") return;
    let cancelled = false;
    const load = () =>
      listAgentRequests()
        .then((loaded) => {
          if (!cancelled) setRows(loaded);
        })
        .catch(() => {
          /* transient — keep what we last knew */
        });
    void load();
    const id = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [given, state]);

  if (state !== "granted") return null;

  const waiting = rows.filter((r) => r.decision?.state === "AWAITING_YOU").length;
  const consider = rows.filter((r) => r.decision?.state === "CONSIDER").length;

  // Nothing to glance at. An empty panel that is always there is furniture.
  if (waiting === 0 && consider === 0) return null;

  return (
    <ComicPanel title="Asked for time">
      <ul className="space-y-1 text-sm">
        {waiting > 0 && <li>{`${waiting} waiting on you`}</li>}
        {consider > 0 && (
          <li className="text-muted-foreground">{`${consider} nobody could take`}</li>
        )}
      </ul>
      <Link
        to="/agent-requests"
        className="mt-3 inline-block text-[11px] underline text-muted-foreground hover:text-foreground"
      >
        Open
      </Link>
    </ComicPanel>
  );
}
